import { RESOURCES, LOG_RESOURCE_MAP } from './resourceConstants';

/**
 * Generates log route permissions dynamically from LOG_RESOURCE_MAP.
 * Each base resource gets a prefix-matched entry covering both
 * /admin/logs/{resource} and /admin/logs/{resource}/log/:id.
 */
const generateLogRoutePermissions = () =>
  Object.entries(LOG_RESOURCE_MAP).map(([baseResource, logResource]) => ({
    path: `/admin/logs/${baseResource}`,
    resource: logResource,
    end: false,
  }));

/**
 * Single source of truth: maps route path patterns to required RBAC resources.
 *
 * Routes NOT listed here are accessible to any authenticated user (e.g. home, profile).
 * Super admins bypass READ checks but NOT REVIEW checks (REQUESTS resource).
 *
 * Matching modes:
 *   - Default (end: true)  — exact path match only.
 *   - end: false           — prefix match (all sub-routes share the same resource).
 *
 * When adding a new admin route:
 *   - If it falls under an existing prefix (e.g. /admin/forms/...) → already covered.
 *   - If it's a new module → add a new entry here.
 */
export const ROUTE_PERMISSIONS = [
  // Requests / Approvals (exact — no sub-routes)
  { path: '/admin/approvals/requests', resource: RESOURCES.REQUESTS },
  { path: '/admin/review/:id', resource: RESOURCES.REQUESTS },

  // Categories (prefix — covers /create, /edit/:id, /:id/organizations, etc.)
  {
    path: '/admin/categories',
    resource: RESOURCES.EXTERNAL_ORGANIZATIONS_CATEGORIES,
    end: false,
  },

  // External Users (prefix)
  {
    path: '/admin/external-users',
    resource: RESOURCES.EXTERNAL_USERS,
    end: false,
  },

  // Forms (prefix)
  { path: '/admin/forms', resource: RESOURCES.FORM, end: false },

  // Subforms (prefix)
  { path: '/admin/subforms', resource: RESOURCES.SUBFORM, end: false },

  // Roles (prefix)
  { path: '/admin/roles', resource: RESOURCES.ROLE_GROUPS, end: false },

  // Admin Users (prefix — covers /create, /edit/:id, /:id/profile)
  { path: '/admin/users', resource: RESOURCES.ADMIN_USERS, end: false },
  // Separate path segment: /admin/user/privileges/:id (singular "user")
  { path: '/admin/user/privileges/:id', resource: RESOURCES.ADMIN_USERS },

  // Workflows (prefix)
  { path: '/admin/workflows', resource: RESOURCES.WORKFLOWS, end: false },

  // Organization Structure (prefix)
  {
    path: '/admin/organization',
    resource: RESOURCES.ORG_STRUCTURE,
    end: false,
  },

  // Notifications & Reminders (exact — no sub-routes)
  { path: '/admin/notifications', resource: RESOURCES.NOTIFICATIONS_SLA },
  { path: '/admin/reminders', resource: RESOURCES.NOTIFICATIONS_SLA },

  // Payments (prefix)
  { path: '/admin/payments', resource: RESOURCES.PAYMENTS, end: false },

  // Audit Logs — generated dynamically from LOG_RESOURCE_MAP
  ...generateLogRoutePermissions(),
];
